import type { User, Recipe } from '../types';
import { isRecipeOwnedByUser } from './recipeFilters';

export function getUserFullName(user: Pick<User, 'firstName' | 'lastName'>): string {
    return `${user.firstName || ''} ${user.lastName || ''}`.trim();
}

export function getUserDisplayName(user: Pick<User, 'nickname' | 'firstName' | 'lastName' | 'email'>): string {
    const fullName = getUserFullName(user);
    return user.nickname || fullName || user.email;
}

export interface UserProfileDisplay {
    primaryLabel: string;
    avatarLabel: string;
    showFullName: boolean;
    fullName: string;
    showEmail: boolean;
    email: string;
    primaryIsEmail: boolean;
}

/** Side menu / profile header lines: name first, email only when no name (always LTR). */
export function getUserProfileDisplay(user: Pick<User, 'nickname' | 'firstName' | 'lastName' | 'email'>): UserProfileDisplay {
    const fullName = getUserFullName(user);
    const nickname = user.nickname?.trim() || '';
    const email = user.email?.trim() || '';

    if (nickname) {
        return {
            primaryLabel: nickname,
            avatarLabel: nickname,
            showFullName: !!fullName && fullName !== nickname,
            fullName,
            showEmail: !!email && email !== nickname,
            email,
            primaryIsEmail: false,
        };
    }

    if (fullName) {
        return {
            primaryLabel: fullName,
            avatarLabel: fullName,
            showFullName: false,
            fullName,
            showEmail: !!email && email !== fullName,
            email,
            primaryIsEmail: false,
        };
    }

    return {
        primaryLabel: email,
        avatarLabel: email || '?',
        showFullName: false,
        fullName: '',
        showEmail: false,
        email,
        primaryIsEmail: !!email,
    };
}

export function buildUserLookup(allUsers: User[], currentUser?: User | null): Map<string, User> {
    const lookup = new Map<string, User>();
    for (const entry of allUsers) {
        if (entry.uid) lookup.set(entry.uid, entry);
    }
    if (currentUser?.uid) {
        lookup.set(currentUser.uid, currentUser);
    }
    return lookup;
}

export function resolveRecipeAuthorName(
    recipe: Pick<Recipe, 'ownerId'>,
    currentUserId: string | undefined,
    userLookup: Map<string, User>,
    labels: { me: string; anonymous: string },
): string {
    const ownerId = recipe.ownerId?.trim();
    if (!ownerId) return labels.anonymous;

    if (isRecipeOwnedByUser(recipe, currentUserId)) {
        return labels.me;
    }

    const owner = userLookup.get(ownerId);
    if (!owner) return labels.anonymous;

    return getUserDisplayName(owner) || labels.anonymous;
}
